import { AbstractTransport } from '@central-products/abstract-transport-bus';
import { LogicRegistry } from './LogicRegistry';
import { sortFeatures } from './sort-features';
import { unifyFeaturesDefinitions } from './unify-features-definitions';
import type { FeatureDefinition, FeatureDefinitionNext, FeatureDependenciesNext } from './types';

const CAN_USE_DOM =
  typeof window !== 'undefined' &&
  typeof window.document !== 'undefined' &&
  typeof window.document.createElement !== 'undefined';

export const bootstrap = (
  transport: AbstractTransport,
  features: FeatureDefinition[],
  featuresConfig: Record<string, object>
): {
  init: () => Promise<void>;
  logicRegistry: LogicRegistry;
  features: Record<string, object>;
} => {
  if (window?.WH?.bootstrapper?.features) {
    throw new Error(`[bootstrapper]: Bootstrapper has been already initialized`);
  }

  // TODO: to be removed in next major
  const logicRegistry = new LogicRegistry();
  const featureRegistry: Record<string, object> = {};

  const registerFeature = (feature: FeatureDefinitionNext) => {
    const config = featuresConfig[feature.name];

    if (!config) {
      throw new Error(`[bootstrapper] Missing feature '${feature.name}' configuration`);
    }

    const dependencies = (feature.dependsOn as FeatureDependenciesNext).reduce((acc, dependency) => {
      acc[dependency.name] = featureRegistry[dependency.name];
      return acc;
    }, ({} as unknown) as Record<string, object | undefined>);
    const instance = feature.create(config, dependencies, transport);

    if (feature.implements && feature.role) {
      const { bindToTransport = true } = feature;
      const implementation = bindToTransport ? transport.get<object>(feature.implements) : instance;

      featureRegistry[feature.role] = implementation;
      logicRegistry.put(feature.implements, implementation);

      if (bindToTransport) {
        transport.bind(instance);
      }
    }

    return { ...feature, instance };
  };

  const registeredFeatures = sortFeatures(unifyFeaturesDefinitions(features)).map(registerFeature);

  if (CAN_USE_DOM) {
    window.WH = window.WH || {};
    window.WH.bootstrapper = { features: featureRegistry };
  }

  return {
    logicRegistry,
    features: featureRegistry,
    init: async () => {
      for (const feature of registeredFeatures) {
        if (feature.init) {
          try {
            await feature.init(feature.instance);
          } catch (error) {
            // tslint:disable-next-line:no-console
            console.error(`[bootstrapper] Failed feature '${feature.name}' initialization: ${error}`);
          }
        }
      }
    },
  };
};
